export type Unit = "currency" | "number" | "percent";
export type Viz = "line" | "bar" | "progress";
export type Period = "7d" | "30d" | "90d" | "12m";

export interface MetricDef {
    key: string;
    label: string;
    unit: Unit;
    // Value per day for "sum" metrics, plain value for "avg" metrics
    base: number;
    volatility: number;
    growth: number;
    aggregate: "sum" | "avg";
    higherIsBetter: boolean;
}

export interface Widget {
    id: string;
    metric: string;
    viz: Viz;
    wide: boolean;
    color: string;
}

export const METRICS: MetricDef[] = [
    { key: "revenue", label: "Revenue", unit: "currency", base: 4200, volatility: 0.25, growth: 0.18, aggregate: "sum", higherIsBetter: true },
    { key: "orders", label: "Orders", unit: "number", base: 58, volatility: 0.3, growth: 0.12, aggregate: "sum", higherIsBetter: true },
    { key: "aov", label: "Avg. Order Value", unit: "currency", base: 72, volatility: 0.08, growth: 0.05, aggregate: "avg", higherIsBetter: true },
    { key: "customers", label: "New Customers", unit: "number", base: 21, volatility: 0.35, growth: 0.2, aggregate: "sum", higherIsBetter: true },
    { key: "conversion", label: "Conversion Rate", unit: "percent", base: 3.4, volatility: 0.12, growth: 0.06, aggregate: "avg", higherIsBetter: true },
    { key: "churn", label: "Churn Rate", unit: "percent", base: 2.1, volatility: 0.15, growth: -0.1, aggregate: "avg", higherIsBetter: false },
    { key: "tickets", label: "Support Tickets", unit: "number", base: 14, volatility: 0.4, growth: -0.08, aggregate: "sum", higherIsBetter: false },
    { key: "nps", label: "NPS Score", unit: "number", base: 46, volatility: 0.06, growth: 0.07, aggregate: "avg", higherIsBetter: true },
];

export const COLORS = ["#6d28d9", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];

export const PERIODS: { key: Period; label: string; points: number; daysPerPoint: number; pointLabel: string }[] = [
    { key: "7d", label: "7 days", points: 7, daysPerPoint: 1, pointLabel: "Day" },
    { key: "30d", label: "30 days", points: 30, daysPerPoint: 1, pointLabel: "Day" },
    { key: "90d", label: "90 days", points: 13, daysPerPoint: 7, pointLabel: "Week" },
    { key: "12m", label: "12 months", points: 12, daysPerPoint: 30, pointLabel: "Month" },
];

export const DEFAULT_WIDGETS: Widget[] = [
    { id: "w1", metric: "revenue", viz: "line", wide: true, color: COLORS[0] },
    { id: "w2", metric: "orders", viz: "bar", wide: false, color: COLORS[1] },
    { id: "w3", metric: "conversion", viz: "progress", wide: false, color: COLORS[2] },
    { id: "w4", metric: "customers", viz: "bar", wide: false, color: COLORS[3] },
    { id: "w5", metric: "churn", viz: "line", wide: false, color: COLORS[4] },
];

// Small deterministic PRNG so every metric/period combo always shows the same numbers
function seededRandom(seed: string) {
    let h = 1779033703 ^ seed.length;
    for (let i = 0; i < seed.length; i++) {
        h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return () => {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        h ^= h >>> 16;
        return (h >>> 0) / 4294967296;
    };
}

export interface MetricSeries {
    current: number[];
    value: number;
    previous: number;
    change: number;
    target: number;
}

export function buildSeries(metric: MetricDef, periodKey: Period): MetricSeries {
    const period = PERIODS.find(p => p.key === periodKey) || PERIODS[1];
    const rand = seededRandom(metric.key + periodKey);
    const total = period.points * 2;
    const values: number[] = [];

    for (let i = 0; i < total; i++) {
        const trend = 1 + metric.growth * (i / total);
        const noise = 1 + (rand() - 0.5) * 2 * metric.volatility;
        const scale = metric.aggregate === "sum" ? period.daysPerPoint : 1;
        values.push(metric.base * scale * trend * noise);
    }

    const previousSeries = values.slice(0, period.points);
    const current = values.slice(period.points);
    const reduce = (list: number[]) => {
        const sum = list.reduce((a, b) => a + b, 0);
        return metric.aggregate === "sum" ? sum : sum / list.length;
    };

    const value = reduce(current);
    const previous = reduce(previousSeries);
    const goal = metric.higherIsBetter ? 1.15 : 0.85;

    return {
        current,
        value,
        previous,
        change: previous === 0 ? 0 : ((value - previous) / previous) * 100,
        target: previous * goal,
    };
}

export function formatValue(value: number, unit: Unit, compact = false): string {
    if (unit === "percent") {
        return `${value.toFixed(1)}%`;
    }
    const options: Intl.NumberFormatOptions = compact
        ? { notation: "compact", maximumFractionDigits: 1 }
        : { maximumFractionDigits: unit === "currency" && value < 1000 ? 2 : 0 };
    const formatted = new Intl.NumberFormat("en-US", options).format(value);
    return unit === "currency" ? `€${formatted}` : formatted;
}
